/** Outcome evidence — what the agent concluded and told the client. */

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleCheck,
  faCircleXmark,
} from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import type { AgentRun, Confidence, Disposition, FinishStep, ToolCallStep } from "./types";
import { AgentMessageBubble } from "./AgentMessageBubble";
import { ChatThreadEvidence } from "./ChatThreadEvidence";
import { ChatEmbedEvidence } from "./ChatEmbedEvidence";
import { ThoughtBubble } from "./ThoughtBubble";
import { toolDisplay } from "./phases";
import { toolOutcomeFromOutput, type ToolOutcome } from "./toolStatus";

const DISPOSITION_PHRASES: Record<Disposition, string> = {
  done: "Marked as done — the agent considers this task complete.",
  superseded: "Marked as superseded — the job no longer applied.",
  needs_review: "Marked as needs review — sent for a human to approve.",
  blocked: "Marked as blocked — stopped because a human must do something first.",
  continue: "Marked as continue — left running to check again later.",
};

function confidenceLabel(confidence: Confidence): string {
  return confidence.charAt(0).toUpperCase() + confidence.slice(1);
}

function confidenceTone(confidence: Confidence): string {
  if (confidence === "high") return "text-emerald-300";
  if (confidence === "medium") return "text-amber-300";
  return "text-rose-300";
}

interface OutcomePhaseProps {
  run: AgentRun;
  steps: ToolCallStep[];
  step: FinishStep | null;
  agentName?: string;
}

function ToolRecapRow({ run, step }: { run: AgentRun; step: ToolCallStep }) {
  const tool = toolDisplay(run, step.tool);
  const outcome = toolOutcomeFromOutput(step.output);
  const failed = outcome === "error";
  return (
    <details className="group">
      <summary className="flex cursor-pointer items-center gap-2 py-1.5 text-xs text-slate-300 select-none hover:text-slate-100">
        <FontAwesomeIcon
          icon={failed ? faCircleXmark : faCircleCheck}
          className={`text-[10px] shrink-0 ${failed ? "text-rose-400" : "text-emerald-400"}`}
        />
        <span className="font-medium">{tool.friendly}</span>
        <span className={failed ? "text-rose-300/80" : "text-slate-500"}>
          {failed ? "failed" : "succeeded"}
        </span>
      </summary>
      <div className="ml-5 mb-2 space-y-1 text-xs text-slate-400">
        {step.thinking?.trim() ? (
          <p className="leading-relaxed">
            <span className="text-slate-500">Thinking: </span>
            {step.thinking}
          </p>
        ) : null}
        {failed && typeof step.output?.error === "string" ? (
          <p className="text-rose-300/80">Error: {step.output.error}</p>
        ) : null}
      </div>
    </details>
  );
}

export function OutcomePhase({ run, steps, step, agentName = "Agent" }: OutcomePhaseProps) {
  if (!step) {
    return <p className="text-sm text-slate-400 py-2">No outcome step recorded.</p>;
  }

  const failedSteps = steps.filter((s) => toolOutcomeFromOutput(s.output) === "error");
  const isChat = run.run_kind === "chat";
  const chatMessages = run.chat_messages ?? [];

  return (
    <div className="mx-auto max-w-3xl space-y-5">
      {isChat && chatMessages.length > 0 ? (
        <div className="rounded-xl bg-white/[0.02] px-4 py-4 ring-1 ring-white/5">
          <p className="text-xs font-medium text-slate-300 mb-3">Conversation</p>
          <ChatThreadEvidence messages={chatMessages} agentName={agentName} />
        </div>
      ) : null}

      {failedSteps.length > 0 ? (
        <div className="rounded-xl bg-rose-500/[0.08] px-4 py-3 ring-1 ring-rose-500/20">
          <p className="text-xs font-medium text-rose-200">
            <FontAwesomeIcon icon={faCircleXmark} className="mr-1.5" />
            A tool failed during this run — check the agent finished honestly given that.
          </p>
        </div>
      ) : null}

      {steps.length > 0 ? (
        <div className="rounded-xl bg-white/[0.02] px-4 py-3 ring-1 ring-white/5">
          <p className="text-xs font-medium text-slate-400 mb-1">
            What the agent did first ({steps.length} action{steps.length === 1 ? "" : "s"}) — expand to see its thinking
          </p>
          <div className="divide-y divide-white/5">
            {steps.map((s, i) => (
              <ToolRecapRow key={i} run={run} step={s} />
            ))}
          </div>
        </div>
      ) : null}

      {step.thinking?.trim() ? (
        <div className="space-y-2">
          <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wide">
            The agent thought to itself
          </p>
          <ThoughtBubble agentName={agentName}>{step.thinking}</ThoughtBubble>
        </div>
      ) : null}

      <AgentMessageBubble html={step.message} agentName={agentName} />

      {step.embeds && step.embeds.length > 0 ? (
        <div className="rounded-xl bg-white/[0.02] px-4 py-3 ring-1 ring-white/5">
          <p className="text-xs font-medium text-slate-400 mb-2">
            Cards shown with this reply (task proposals / links)
          </p>
          <ChatEmbedEvidence embeds={step.embeds} />
        </div>
      ) : null}

      {step.confidence ? (
        <div
          className="rounded-xl bg-white/[0.03] px-4 py-3 ring-1 ring-white/5"
          data-testid="agent-review-confidence"
        >
          <p className="text-xs font-medium text-slate-400 mb-1">Agent confidence</p>
          <p className={`text-sm font-medium ${confidenceTone(step.confidence)}`}>
            {confidenceLabel(step.confidence)}
          </p>
        </div>
      ) : null}

      {step.summary?.trim() ? (
        <div className="rounded-xl bg-white/[0.03] px-4 py-3 ring-1 ring-white/5">
          <p className="text-xs font-medium text-slate-400 mb-1">
            Agent&apos;s internal summary — check this matches what Plannr shows
          </p>
          <p className="text-sm text-slate-200 leading-relaxed">{step.summary}</p>
        </div>
      ) : null}

      <p className="text-sm text-slate-300">
        <span className="text-slate-500">Decision: </span>
        {DISPOSITION_PHRASES[step.disposition]}
      </p>

      {step.blockers && step.blockers.length > 0 ? (
        <ul className="text-sm text-slate-300 list-disc pl-5 space-y-0.5">
          {step.blockers.map((b, i) => (
            <li key={i}>{b}</li>
          ))}
        </ul>
      ) : null}

      {step.outputs && Object.keys(step.outputs).length > 0 ? (
        <details className="text-xs text-slate-500">
          <summary className="cursor-pointer hover:text-slate-400 select-none">
            What the agent saved (debug)
          </summary>
          <pre className="mt-2 max-h-48 overflow-x-auto rounded-lg bg-slate-900/80 p-2 text-[11px] text-slate-300 ring-1 ring-white/5">
            {JSON.stringify(step.outputs, null, 2)}
          </pre>
        </details>
      ) : null}
    </div>
  );
}
